import { useEffect, useRef } from "react";

/**
 * 光速星线跃迁（UI-SPACE-01b，用户 10-08 给的参考：星星从中心往外拉成光线、越拉越快、最后闪亮落到新界面）。
 * 只用 canvas 2D 自己画，不加素材、不装包。放在一个定位容器里，铺满它。
 *
 * 保证（I 10-08 要求）：
 * - 减少动态、拿不到 canvas（jsdom）：不画，下一拍直接 onDone；
 * - 一定结束：时长到了就 onDone；rAF 停了（切后台）有兜底计时器；页面切到后台立即结束；
 * - onDone 只调一次；卸载时清掉计时器和 rAF；
 * - 掉帧自动降级：连续几帧太慢先减一半星数，再慢就直接收尾，不卡在半路；
 * - 不盖住底下：每帧擦干净、不铺底色，只有最后淡到落地页底色那一段才整张填色。
 */
export type WarpStreaksProps = {
  /** 变成 true 时开始；之后改回 false 不会打断（跃迁一旦开始就跑完） */
  run: boolean;
  /** 总时长（含最后的闪亮），夹在 600–2400ms */
  durationMs?: number;
  /** 结束时调用一次（落地） */
  onDone: () => void;
  /** 收尾淡入用的颜色（落地页的底色）；不给就读 --c-bg */
  flashColor?: string;
  className?: string;
};

export const WARP_MIN_MS = 600;
export const WARP_MAX_MS = 2400;
export const WARP_DEFAULT_MS = 1600; // REQ13 §5：约 1.2–2 秒
/** 兜底：时长之外再等这么久还没结束就强制落地（后台标签页 rAF 停掉时靠它） */
export const WARP_GRACE_MS = 600;
export const clampWarpMs = (ms: number | undefined) =>
  Math.min(WARP_MAX_MS, Math.max(WARP_MIN_MS, Number.isFinite(ms) ? (ms as number) : WARP_DEFAULT_MS));

/** 星数：按面积，手机上不超过 260；降级时减半 */
export function starCountFor(width: number, height: number) {
  return Math.max(60, Math.min(260, Math.round((width * height) / 1800)));
}

/** 速度曲线：前慢后快（三次）；最后 35% 慢慢淡到落地页底色（REQ13 §5：不快速闪白，落地保留锚点） */
export const warpSpeed = (p: number) => p * p * p;
export const FLASH_FROM = 0.65;
export const flashAlpha = (p: number) => { if (p <= FLASH_FROM) return 0; const k = Math.min(1, (p - FLASH_FROM) / (1 - FLASH_FROM)); return k * k * (3 - 2 * k); };

type Star = { a: number; r: number; warm: boolean };

function reducedMotion() {
  try {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function WarpStreaks({ run, durationMs, onDone, flashColor, className }: WarpStreaksProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const doneRef = useRef(false);
  const startedRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  // 时长与闪亮色在开始那一刻读定：之后属性变化不重启、也不打断（跃迁一旦开始就跑完）
  const optsRef = useRef({ durationMs, flashColor });
  optsRef.current = { durationMs, flashColor };
  // 收尾清理只在卸载时做：run 中途改回 false 也不打断、不丢兜底计时器
  const cleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cleanupRef.current?.(), []);

  useEffect(() => {
    if (!run || startedRef.current) return;
    startedRef.current = true;
    const finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      onDoneRef.current();
    };
    const { durationMs: wantMs, flashColor: wantFlash } = optsRef.current;
    const total = clampWarpMs(wantMs);
    const canvas = canvasRef.current;
    const ctx = canvas && !reducedMotion() ? (() => { try { return canvas.getContext("2d"); } catch { return null; } })() : null;
    if (!canvas || !ctx) {
      const t = window.setTimeout(finish, 0);
      cleanupRef.current = () => window.clearTimeout(t);
      return;
    }
    const css = getComputedStyle(canvas);
    const flash = wantFlash || css.getPropertyValue("--c-bg").trim() || "#f6f1e7"; // 退路＝tokens.css 浅色 --c-bg
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const box = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(box.width)), h = Math.max(1, Math.round(box.height));
    canvas.width = w * dpr; canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    const cx = w / 2, cy = h / 2, R = Math.hypot(cx, cy);
    let stars: Star[] = Array.from({ length: starCountFor(w, h) }, () => {
      const r = Math.random() * 0.9 + 0.02;
      return { a: Math.random() * Math.PI * 2, r, warm: Math.random() < 0.35 };
    });
    let raf = 0, last = 0, start = 0, slow = 0, degraded = false;
    const guard = window.setTimeout(finish, total + WARP_GRACE_MS);
    const onHidden = () => { if (document.visibilityState === "hidden") finish(); };
    document.addEventListener("visibilitychange", onHidden);
    const frame = (now: number) => {
      if (doneRef.current) return;
      if (!start) { start = now; last = now; }
      const dt = Math.min(64, now - last); last = now;
      const p = Math.min(1, (now - start) / total);
      if (dt > 34) slow += 1; else slow = Math.max(0, slow - 1);
      if (slow >= 4 && !degraded) { degraded = true; stars = stars.filter((_, i) => i % 2 === 0); slow = 0; }
      else if (slow >= 4 && degraded) { finish(); return; }
      // 每帧整张擦干净、不铺底色：底下的宇宙和正在拉近的地球一直看得见（10-09 逐帧截图发现旧的拖影底色几帧就把地球盖成一片纯夜色）。
      // 拖影改成每条线自己画：头亮、尾巴渐隐，长度随速度变长。
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, w, h);
      const sp = warpSpeed(p);
      const v = (0.08 + 3.2 * sp) * (dt / 1000);
      const tail = 0.015 + 0.3 * sp;
      ctx.lineCap = "round";
      for (const s of stars) {
        s.r += v * (0.25 + s.r);
        if (s.r > 1.15) s.r = Math.random() * 0.12 + 0.01;
        const r0 = Math.max(0, s.r - tail * (0.4 + s.r));
        const x0 = cx + Math.cos(s.a) * r0 * R, y0 = cy + Math.sin(s.a) * r0 * R;
        const x1 = cx + Math.cos(s.a) * s.r * R, y1 = cy + Math.sin(s.a) * s.r * R;
        const color = s.warm ? "#ffe1a3" : "#fbf7ef"; // 暖星＝globeModel.ts 星点暖色；奶白＝tokens.css 浅色 --c-on-deep
        const g = ctx.createLinearGradient(x0, y0, x1, y1);
        g.addColorStop(0, `${color}00`);
        g.addColorStop(1, color);
        ctx.globalAlpha = Math.min(1, 0.25 + s.r);
        ctx.strokeStyle = g;
        ctx.lineWidth = 0.6 + 1.6 * s.r;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      }
      const fa = flashAlpha(p);
      if (fa > 0) { ctx.globalAlpha = fa; ctx.fillStyle = flash; ctx.fillRect(0, 0, w, h); }
      if (p >= 1) { finish(); return; }
      raf = window.requestAnimationFrame(frame);
    };
    raf = window.requestAnimationFrame(frame);
    cleanupRef.current = () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(guard);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [run]);

  return <canvas ref={canvasRef} className={className ?? "ps-warp-streaks"} aria-hidden="true" data-warp={run ? "run" : "idle"} />;
}
